import React from 'react';
import type { SocialDisplayItem } from '../types';
import { EmptyLayer } from './LayerPlayer';

interface LayerSocialProps {
  accentColor: string;
  socials: SocialDisplayItem[];
  hasSocialsLayer: boolean;
  artistName?: string;
  compact?: boolean;
  onSnsClick?: (platform: string) => void;
}

export function LayerSocial({
  accentColor,
  socials,
  hasSocialsLayer,
  artistName = '',
  compact = false,
  onSnsClick,
}: LayerSocialProps) {
  if (!hasSocialsLayer) {
    return (
      <EmptyLayer
        accentColor={accentColor}
        layerLabel="Layer 04 / Social"
        icon="ri-share-line"
      />
    );
  }

  const gridCols = compact ? 'grid-cols-1' : 'grid-cols-3';
  const iconSize = compact ? 'w-10 h-10' : 'w-12 h-12';
  const iconFontSize = compact ? 'text-lg' : 'text-xl';
  const itemPadding = compact ? 'px-4 py-4' : 'px-6 py-5';

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url?: string,
    name?: string
  ) => {
    if (!url?.startsWith('http')) {
      e.preventDefault();
      return;
    }
    if (url && name) {
      onSnsClick?.(name);
    }
  };

  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
        />
        <p className="text-gray-500 text-xs tracking-widest uppercase">
          Layer 04 / Social
        </p>
      </div>

      {/* グラスモーフィズムパネル */}
      <div
        className="rounded-2xl p-5 border backdrop-blur-md"
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderColor: `${accentColor}18`,
          boxShadow: `0 0 40px ${accentColor}08`,
        }}
      >
        <div className={`grid ${gridCols} gap-3`}>
          {socials.map((social, index) => {
            const isValidUrl = social.url?.startsWith('http') ?? false;
            const href = isValidUrl ? social.url! : '#';
            const isVerified = (social as any).verified === true;

            return (
              <a
                key={index}
                href={href}
                target={isValidUrl ? '_blank' : undefined}
                rel="nofollow noopener noreferrer"
                onClick={(e) => handleClick(e, social.url, social.name)}
                className={`flex items-center gap-4 ${itemPadding} rounded-xl border transition-all duration-300 group cursor-pointer relative`}
                style={{
                  background: 'rgba(0,0,0,0.4)',
                  borderColor: 'rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(8px)',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = `${accentColor}60`;
                  el.style.background = `${accentColor}08`;
                  el.style.boxShadow = `0 8px 30px ${accentColor}18, inset 0 0 20px ${accentColor}06`;
                  el.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = 'rgba(255,255,255,0.06)';
                  el.style.background = 'rgba(0,0,0,0.4)';
                  el.style.boxShadow = 'none';
                  el.style.transform = 'translateY(0)';
                }}
              >
                <div
                  className={`${iconSize} rounded-full border flex items-center justify-center flex-shrink-0 transition-all relative`}
                  style={{
                    background: `${accentColor}18`,
                    borderColor: `${accentColor}40`,
                    boxShadow: `0 0 15px ${accentColor}18`,
                  }}
                >
                  <i className={`${social.icon} ${iconFontSize}`} style={{ color: accentColor }} />
                  {isVerified && (
                    <div
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-black flex items-center justify-center animate-pulse"
                      style={{ boxShadow: '0 0 12px rgba(59, 130, 246, 0.8)' }}
                      title="公式認証済み"
                    >
                      <i className="ri-check-line text-[10px] text-white font-bold" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold block truncate">{social.name}</span>
                    {isVerified && (
                      <div
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/50"
                        style={{ boxShadow: '0 0 8px rgba(59, 130, 246, 0.3)' }}
                      >
                        <i className="ri-verified-badge-fill text-[10px] text-blue-400" />
                        <span className="text-blue-400 text-[9px] font-mono tracking-wider">OFFICIAL</span>
                      </div>
                    )}
                  </div>
                  <span className="text-gray-500 text-sm truncate block">
                    {social.username ||
                      social.url ||
                      `@${artistName.toLowerCase().replace(/\s/g, '')}`}
                  </span>
                </div>

                {isValidUrl && (
                  <i className="ri-external-link-line text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                )}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
